// /////////////////////////////////////////////////////////////
// [ファイル] CustomerMergeDialog.tsx
// [概要] 顧客統合 (マージ) ダイアログ。重複した 2 顧客のうち残す側へ、消す側を参照する
//        全データ (SFA 案件・建物関係・イベント案内先・先方担当者・連絡先・名寄せ) を付け替え、
//        消す側を削除する。
//        ステップ 1 相手を検索して選ぶ (同種別のみ、自分自身は除外) → 残す側を選ぶ
//        ステップ 2 プレビュー (主要項目の見比べ表・同一判定・付替件数・衝突項目) を確認 → 統合を実行
//        同一判定 (氏名/カナ/電話/メール/住所/ターコイズコード/事業者登録番号のいずれか一致) が
//        1 つも成立しない場合は赤い警告を出し、「別の相手先ではない」チェックを入れるまで統合できない。
//        実行後は残した側の顧客 ID を親へ返す (親が再取得または遷移する)。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useAuth } from '../firebase/AuthProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useNotification } from '../providers/NotificationProvider';
import { ep } from '../api/endpoints';

const SEARCH_URL = ep('search_crm_customers');
const PREVIEW_URL = ep('preview_crm_customer_merge');
const MERGE_URL = ep('merge_crm_customers');

interface CustomerOption {
  id: string;
  customer_type: 'individual' | 'corporate';
  display_name: string;
  employer_name?: string | null;
}

// プレビュー API が返す見比べ用プロフィール
interface MergeProfile {
  name: string;
  kana: string;
  birth_date?: string | null;
  representative_name?: string | null;
  employer_name?: string | null;
  post_number: string;
  address: string;
  phones: string[];
  emails: string[];
  staff: string;
  turquoise_cds: string[];
  invoice_reg_no: string;
  registration_source: string;
  created_at: string;
}

interface MergeSide {
  id: string;
  display_name: string;
  counts: Record<string, number>;
  profile: MergeProfile;
}

interface MergePreview {
  customer_type: string;
  source: MergeSide;
  target: MergeSide;
  conflicts: { key: string; message: string }[];
  similarity: { is_likely_same: boolean; matches: { key: string; label: string; value: string }[] };
}

const COUNT_LABELS: { key: string; label: string }[] = [
  { key: 'sfa_projects', label: 'SFA 案件' },
  { key: 'sfa_purchase_contracts', label: 'SFA 仕入契約 (売主・仲介)' },
  { key: 'sfa_sales_units', label: 'SFA 販売区画 (顧客・仲介)' },
  { key: 'building_relations', label: '建物関係' },
  { key: 'event_invitees', label: 'イベント案内先' },
  { key: 'event_participants', label: 'イベント参加者' },
  { key: 'employer_refs', label: '先方担当者 (勤務先参照)' },
  { key: 'contacts', label: '連絡先' },
  { key: 'turquoise_codes', label: 'ターコイズコード' },
  { key: 'turquoise_match', label: '名寄せ履歴' },
  { key: 'mail_subscriptions', label: 'メール配信設定' },
  { key: 'mail_deliveries', label: 'メール送信履歴' },
];

// 見比べ表の行定義。matchKey は類似判定の一致キー (一致した行を強調する)
interface CompareRow {
  label: string;
  matchKey?: string;
  get: (p: MergeProfile) => string;
  onlyFor?: 'individual' | 'corporate';
}

const COMPARE_ROWS: CompareRow[] = [
  { label: '氏名・法人名', matchKey: 'name', get: (p) => p.name },
  { label: 'フリガナ', matchKey: 'kana', get: (p) => p.kana },
  { label: '生年月日', get: (p) => p.birth_date || '', onlyFor: 'individual' },
  { label: '代表者', get: (p) => p.representative_name || '', onlyFor: 'corporate' },
  { label: '電話番号', matchKey: 'phone', get: (p) => p.phones.join(' / ') },
  { label: 'メール', matchKey: 'email', get: (p) => p.emails.join(' / ') },
  { label: '住所', matchKey: 'address', get: (p) => [p.post_number, p.address].filter(Boolean).join(' ') },
  { label: '勤務先', get: (p) => p.employer_name || '', onlyFor: 'individual' },
  { label: '当社担当', get: (p) => p.staff },
  { label: 'ターコイズコード', matchKey: 'turquoise_cd', get: (p) => p.turquoise_cds.join(' / ') },
  { label: '事業者登録番号', matchKey: 'invoice_reg_no', get: (p) => p.invoice_reg_no },
  { label: '登録元 / 登録日', get: (p) => [p.registration_source, p.created_at].filter(Boolean).join(' / ') },
];

// 確認ダイアログの本文に載せる主要項目 (名前だけでなく中身で見比べられるようにする)
const CONFIRM_ROWS: CompareRow[] = COMPARE_ROWS.filter((r) =>
  ['氏名・法人名', 'フリガナ', '電話番号', '住所', '当社担当'].includes(r.label),
);

export interface CustomerMergeDialogProps {
  open: boolean;
  onClose: () => void;
  currentCustomerId: string;
  currentCustomerType: 'individual' | 'corporate';
  currentDisplayName: string;
  onMerged: (targetId: string) => void;
}

const CustomerMergeDialog: React.FC<CustomerMergeDialogProps> = ({
  open,
  onClose,
  currentCustomerId,
  currentCustomerType,
  currentDisplayName,
  onMerged,
}) => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const notify = useNotification();

  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<CustomerOption | null>(null);
  const [keep, setKeep] = useState<'current' | 'selected'>('current');
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [merging, setMerging] = useState(false);
  // 同一判定が成立しない場合に操作者が明示的に確認したか
  const [acknowledged, setAcknowledged] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (open) {
      setOptions([]);
      setSelected(null);
      setKeep('current');
      setPreview(null);
      setPreviewError(null);
      setAcknowledged(false);
    }
  }, [open]);

  // 相手検索 (同種別のみ・自分自身は除外)。debounce 300ms + 連番で古い応答を捨てる
  const handleSearch = (query: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!query.trim()) {
      setOptions([]);
      return;
    }
    setSearching(true);
    const seq = ++seqRef.current;
    timerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(SEARCH_URL, { query });
        if (seq !== seqRef.current) return;
        const results: CustomerOption[] = res.data.status === 'success' ? res.data.results || [] : [];
        setOptions(results.filter((o) => o.customer_type === currentCustomerType && o.id !== currentCustomerId));
      } catch {
        if (seq === seqRef.current) setOptions([]);
      } finally {
        if (seq === seqRef.current) setSearching(false);
      }
    }, 300);
  };

  const sourceId = keep === 'current' ? selected?.id : currentCustomerId;
  const targetId = keep === 'current' ? currentCustomerId : selected?.id;

  // 相手と残す側が決まったらプレビューを取得 (相手が変わるたびに確認チェックはリセット)
  useEffect(() => {
    setAcknowledged(false);
    if (!open || !selected || !sourceId || !targetId) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError(null);
    axios
      .post(PREVIEW_URL, { sourceCustomerId: sourceId, targetCustomerId: targetId })
      .then((res) => {
        if (cancelled) return;
        if (res.data.status === 'success') setPreview(res.data.preview);
        else setPreviewError(res.data.message || 'プレビューの取得に失敗しました');
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewError(err?.response?.data?.message || 'プレビューの取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected?.id, keep]);

  const likelySame = preview?.similarity?.is_likely_same ?? false;
  const canMerge = !!preview && !loadingPreview && !merging && (likelySame || acknowledged);

  // 確認ダイアログ本文 (DialogContentText は <p> なので span のグリッドで組む)
  const buildConfirmMessage = (pv: MergePreview): React.ReactNode => {
    const rows = CONFIRM_ROWS.filter((r) => !r.onlyFor || r.onlyFor === pv.customer_type);
    return (
      <Box component="span" sx={{ display: 'block' }}>
        <Box component="span" sx={{ display: 'block', mb: 1 }}>
          「{pv.source.display_name}」を「{pv.target.display_name}」へ統合します。
        </Box>
        <Box
          component="span"
          sx={{
            display: 'grid',
            gridTemplateColumns: '6em 1fr 1fr',
            columnGap: 1,
            rowGap: 0.25,
            fontSize: '0.8rem',
            mb: 1,
            '& > span': { display: 'block', wordBreak: 'break-all' },
          }}
        >
          <Box component="span" sx={{ color: 'text.secondary' }}>項目</Box>
          <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>消す側</Box>
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>残す側</Box>
          {rows.map((r) => (
            <React.Fragment key={r.label}>
              <Box component="span" sx={{ color: 'text.secondary' }}>{r.label}</Box>
              <Box component="span">{r.get(pv.source.profile) || '—'}</Box>
              <Box component="span">{r.get(pv.target.profile) || '—'}</Box>
            </React.Fragment>
          ))}
        </Box>
        {!pv.similarity.is_likely_same && (
          <Box component="span" sx={{ display: 'block', color: 'error.main', fontWeight: 600, mb: 1 }}>
            主要項目が 1 つも一致していません。別の相手先でないことを確認済みですか？
          </Box>
        )}
        <Box component="span" sx={{ display: 'block' }}>
          消す側の顧客は削除され、案件・建物・案内先・連絡先などの参照は残す側へ付け替えられます。
          この操作は取り消せません。よろしいですか？
        </Box>
      </Box>
    );
  };

  const handleMerge = async () => {
    if (!preview || !sourceId || !targetId || !canMerge) return;
    const ok = await confirm({
      title: '顧客を統合',
      message: buildConfirmMessage(preview),
      destructive: true,
      confirmText: '統合する',
      cancelText: 'キャンセル',
    });
    if (!ok) return;
    setMerging(true);
    try {
      const res = await axios.post(MERGE_URL, {
        sourceCustomerId: sourceId,
        targetCustomerId: targetId,
        operator_id: user?.email,
      });
      if (res.data.status === 'success') {
        notify.success('顧客を統合しました');
        onMerged(targetId);
        onClose();
      } else {
        notify.error(res.data.message || '統合に失敗しました');
      }
    } catch (err: any) {
      console.error('Error merging customers:', err);
      notify.error(err?.response?.data?.message || '統合に失敗しました');
    } finally {
      setMerging(false);
    }
  };

  const optionLabel = (o: CustomerOption) =>
    o.employer_name ? `${o.display_name}（${o.employer_name}）` : o.display_name || '';

  const matchedKeys = new Set((preview?.similarity?.matches || []).map((m) => m.key));
  const compareRows = COMPARE_ROWS.filter((r) => !r.onlyFor || r.onlyFor === preview?.customer_type);

  return (
    <Dialog open={open} onClose={merging ? undefined : onClose} maxWidth="md" fullWidth disableScrollLock>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icons.CallMerge sx={{ fontSize: 18 }} /> 他の顧客と統合
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 1.5 }}>
          同じ{currentCustomerType === 'individual' ? '個人' : '法人'}顧客を検索して統合します。
          残す側に案件・建物・案内先・連絡先などの参照を付け替え、消す側は削除されます（履歴は保持）。
        </Typography>

        <Autocomplete
          options={options}
          loading={searching}
          value={selected}
          filterOptions={(x) => x}
          getOptionLabel={optionLabel}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText="氏名・会社名で検索してください"
          onInputChange={(_, v, reason) => {
            if (reason === 'input') handleSearch(v);
          }}
          onChange={(_, v) => setSelected(v)}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">{optionLabel(o)}</Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="統合する相手を検索"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {selected && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              残す顧客
            </Typography>
            <RadioGroup value={keep} onChange={(e) => setKeep(e.target.value as 'current' | 'selected')}>
              <FormControlLabel
                value="current"
                control={<Radio size="small" />}
                label={<Typography variant="body2">この顧客を残す（{currentDisplayName}）</Typography>}
              />
              <FormControlLabel
                value="selected"
                control={<Radio size="small" />}
                label={<Typography variant="body2">選んだ顧客を残す（{selected.display_name}）</Typography>}
              />
            </RadioGroup>
          </Box>
        )}

        {loadingPreview && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">付け替え内容を確認中...</Typography>
          </Box>
        )}
        {previewError && (
          <Alert severity="error" sx={{ mt: 2 }}>{previewError}</Alert>
        )}

        {preview && !loadingPreview && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip label={`消す側 ${preview.source.display_name}`} size="small" color="error" variant="outlined" sx={{ height: 22 }} />
              <Icons.ArrowForward sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Chip label={`残す側 ${preview.target.display_name}`} size="small" color="primary" variant="outlined" sx={{ height: 22 }} />
            </Box>

            {/* 同一判定 */}
            {likelySame ? (
              <Alert severity="success" icon={<Icons.CheckCircleOutline fontSize="inherit" />} sx={{ mb: 1.5, fontSize: '0.8rem', py: 0.25 }}>
                同一の可能性があります（一致
                {preview.similarity.matches.map((m) => `${m.label}「${m.value}」`).join('、')}）
              </Alert>
            ) : (
              <Alert severity="error" icon={<Icons.WarningAmber fontSize="inherit" />} sx={{ mb: 1.5, fontSize: '0.8rem', py: 0.25 }}>
                氏名・フリガナ・電話番号・メール・住所・ターコイズコード・事業者登録番号のいずれも一致しません。
                別の相手先を選んでいる可能性があります。下の見比べ表で内容を確認してください。
              </Alert>
            )}

            {/* 主要項目の見比べ表 */}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              主要項目の見比べ（緑の行は一致）
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': { py: 0.4, fontSize: '0.8rem', wordBreak: 'break-all', verticalAlign: 'top' },
                  '& .MuiTableCell-head': { fontWeight: 600, whiteSpace: 'nowrap' },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '9em' }}>項目</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>消す側</TableCell>
                    <TableCell sx={{ color: 'primary.main' }}>残す側</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {compareRows.map((r) => {
                    const matched = !!r.matchKey && matchedKeys.has(r.matchKey);
                    return (
                      <TableRow key={r.label} sx={matched ? { bgcolor: 'rgba(46, 125, 50, 0.08)' } : undefined}>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {matched && <Icons.CheckCircle sx={{ fontSize: 13, color: 'success.main', mr: 0.5, verticalAlign: 'text-bottom' }} />}
                          {r.label}
                        </TableCell>
                        <TableCell>{r.get(preview.source.profile) || '—'}</TableCell>
                        <TableCell>{r.get(preview.target.profile) || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* 付替件数 */}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 1.5 }}>
              付け替え件数
            </Typography>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.4, fontSize: '0.8rem', whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>項目</TableCell>
                  <TableCell align="right">消す側（付替）</TableCell>
                  <TableCell align="right">残す側（既存）</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {COUNT_LABELS.filter(({ key }) => key in preview.source.counts).map(({ key, label }) => (
                  <TableRow key={key}>
                    <TableCell>{label}</TableCell>
                    <TableCell align="right">{preview.source.counts[key] ?? 0}</TableCell>
                    <TableCell align="right">{preview.target.counts[key] ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {preview.conflicts.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.8rem' }}>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {preview.conflicts.map((c) => (
                    <li key={c.key}>{c.message}</li>
                  ))}
                </Box>
              </Alert>
            )}

            {/* 同一判定が成立しない場合の明示確認 */}
            {!likelySame && (
              <FormControlLabel
                sx={{ mt: 1.5, alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    size="small"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    sx={{ py: 0, color: 'error.main', '&.Mui-checked': { color: 'error.main' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'error.main', fontWeight: 600 }}>
                    見比べ表を確認し、別の相手先ではない（同一の顧客である）ことを確認しました
                  </Typography>
                }
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={merging} sx={STANDARD_TEXT_BUTTON_SX}>
          キャンセル
        </Button>
        <Button
          onClick={handleMerge}
          disabled={!canMerge}
          startIcon={<Icons.CallMerge />}
          sx={STANDARD_CTA_BUTTON_SX}
        >
          {merging ? '統合中...' : '統合する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerMergeDialog;
